import { useMemo, useState } from 'react';
import { GanttWorklog, GanttDeliverable, GanttEngagement, GanttChildIssue } from '../lib/api';
import { fmtCurrency, fmtHours } from '../lib/fmt';
import { IssueTypeBadge } from './ui';
import { useConfig, jiraIssueUrl } from '../hooks/useConfig';

/**
 * Gantt-style timeline with recursive expand/collapse layers.
 *
 * Every node in the hierarchy — group or leaf — renders its own aggregate
 * bar, density ticks, and shipped/due markers. Clicking a group toggles
 * expand/collapse. Aggregations roll up from leaves: total hours, first/last
 * work date, and whether ALL descendants are shipped (= solid green bar) vs
 * any still open (= amber).
 *
 * Hierarchies:
 *   staff       → Engagement → Deliverable (leaf = one deliverable the subject
 *                                            touched or manages).
 *   team        → Member → Engagement → Deliverable (leaf = member × deliverable).
 *   engagement  → Engagement → Deliverable/Overhead → Package/Task (leaf = task-level
 *                                                           roll-up across its
 *                                                           members on that
 *                                                           deliverable).
 */

type Mode = 'staff' | 'team' | 'engagement';

interface Props {
  mode: Mode;
  from: string;
  to: string;
  worklogs: GanttWorklog[];
  deliverables: GanttDeliverable[];
  childIssues: GanttChildIssue[];
  engagements: GanttEngagement[];
}

// ─── Date helpers ────────────────────────────────────────────────────────────

function toDate(s: string): Date {
  const [y, m, d] = s.split('-').map((n) => parseInt(n));
  return new Date(Date.UTC(y, m - 1, d));
}
function toYMD(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}
function addDays(d: Date, n: number): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + n));
}
function daysBetween(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}
function startOfMonth(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
}
function minStr(a: string | null, b: string | null): string | null {
  if (!a) return b; if (!b) return a; return a < b ? a : b;
}
function maxStr(a: string | null, b: string | null): string | null {
  if (!a) return b; if (!b) return a; return a > b ? a : b;
}

// ─── Node tree ───────────────────────────────────────────────────────────────
//
// A single type for both group and leaf nodes. Leaves set deliverable/userId.
// Groups have children and optional shippedMarker (for engagement-level roll-up).

interface Node {
  key: string;
  label: string;
  subLabel?: string;                 // secondary metadata for the leaf label row
  jiraIssueKey?: string;             // populated on deliverable-leaf rows
  issueType?: 'Deliverable' | 'Overhead';
  children: Node[];                  // [] for leaves
  // Aggregated (live once computed in aggregate()):
  earliestDate: string | null;
  latestDate: string | null;
  anyOpen: boolean;                  // at least one descendant is still open
  anyShipped: boolean;               // at least one descendant has a shipped date
  shippedMarker: string | null;      // own shipped date (or latest descendant's if all shipped)
  dueDate: string | null;            // latest descendant's due date (for the hollow marker)
  // Planned vs actual bars (option C). Target span = planned kickoff → due_date.
  // Actual span  = actual kickoff → shipped (or latest activity).
  targetStart: string | null;        // earliest planned kickoff in subtree
  targetEnd:   string | null;        // latest due_date in subtree
  actualStart: string | null;        // earliest actual kickoff / first worklog
  actualEnd:   string | null;        // latest shipped / last worklog / today
  hoursByDate: Map<string, number>;
  totalHours: number;
  totalValue: number;
}

function newNode(key: string, label: string, extra: Partial<Node> = {}): Node {
  return {
    key, label,
    children: [],
    earliestDate: null, latestDate: null,
    anyOpen: false, anyShipped: false,
    shippedMarker: null, dueDate: null,
    targetStart: null, targetEnd: null,
    actualStart: null, actualEnd: null,
    hoursByDate: new Map(),
    totalHours: 0, totalValue: 0,
    ...extra,
  };
}

// ─── Pivot ───────────────────────────────────────────────────────────────────

function pivot(
  mode: Mode,
  worklogs: GanttWorklog[],
  deliverables: GanttDeliverable[],
  childIssues: GanttChildIssue[],
  engagements: GanttEngagement[],
): Node[] {
  const engById = new Map(engagements.map((e) => [e.jira_issue_id, e]));
  const delivById = new Map(deliverables.map((d) => [d.jira_issue_id, d]));

  // Index child issues by their parent deliverable. Used to decompose the
  // traditional "deliverable = leaf" pattern into "deliverable = group with
  // one leaf per Package/Task". A deliverable with no children keeps its
  // single-leaf form (no spurious indirection).
  const childrenByDeliv = new Map<string, GanttChildIssue[]>();
  for (const c of childIssues) {
    const arr = childrenByDeliv.get(c.parent_deliverable_jira_issue_id);
    if (arr) arr.push(c); else childrenByDeliv.set(c.parent_deliverable_jira_issue_id, [c]);
  }

  // Collect worklogs per (user, deliverable). Engagement mode no longer
  // groups by team — the tree is engagement → deliverable → task leaves,
  // with every author's hours folded into the deliverable row directly.
  const wlByUserDeliv = new Map<string, GanttWorklog[]>();
  for (const w of worklogs) {
    const ku = `${w.author_jira_user_id}|${w.deliverable_jira_issue_id}`;
    (wlByUserDeliv.get(ku) ?? (wlByUserDeliv.set(ku, []).get(ku)!)).push(w);
  }

  /**
   * Expands a set of worklogs on a deliverable into per-task leaves. Returns:
   *   - if the deliverable has NO children (old structure) → a single leaf
   *     aggregating all worklogs (backward-compatible).
   *   - if the deliverable HAS children → a group node with one leaf per
   *     child that either has worklogs or exists in the data, plus a "(logged
   *     directly)" leaf for worklogs logged against the deliverable itself.
   *
   * `keyPrefix` ensures uniqueness in team/engagement modes where the same
   *  deliverable can appear under multiple members/teams.
   */
  function makeDeliverableNode(
    d: GanttDeliverable,
    wls: GanttWorklog[],
    keyPrefix: string,
    extra: Partial<Node> = {},
  ): Node {
    const children = childrenByDeliv.get(d.jira_issue_id) ?? [];
    if (children.length === 0) {
      // No child issues known — behave exactly as before.
      const leaf = newNode(`${keyPrefix}${d.jira_issue_id}`, d.summary, {
        jiraIssueKey: d.jira_issue_key,
        issueType: d.issue_type,
        ...extra,
      });
      applyWorklogs(leaf, wls);
      applyDeliverable(leaf, d);
      return leaf;
    }
    // Group node carrying the deliverable metadata. Task leaves hang below.
    const group = newNode(`${keyPrefix}${d.jira_issue_id}`, d.summary, {
      jiraIssueKey: d.jira_issue_key,
      issueType: d.issue_type,
      ...extra,
    });
    applyDeliverable(group, d);

    // Partition worklogs: per child_id, plus a "direct" bucket for worklogs
    // logged against the deliverable itself (not a child).
    const byChild = new Map<string, GanttWorklog[]>();
    const direct: GanttWorklog[] = [];
    for (const w of wls) {
      if (w.source_issue_jira_issue_id === d.jira_issue_id) { direct.push(w); continue; }
      const arr = byChild.get(w.source_issue_jira_issue_id);
      if (arr) arr.push(w); else byChild.set(w.source_issue_jira_issue_id, [w]);
    }
    // One leaf per child issue. Always emitted (even with zero worklogs in
    // the window) so the user sees the full structure.
    for (const child of children) {
      const cLeaf = newNode(`${keyPrefix}${d.jira_issue_id}|c:${child.jira_issue_id}`, child.summary, {
        jiraIssueKey: child.jira_issue_key,
        // IssueType badge is reserved for Deliverable/Overhead; tasks/packages
        // are labelled via subLabel instead.
        subLabel: child.issue_type,
      });
      const cWls = byChild.get(child.jira_issue_id) ?? [];
      applyWorklogs(cLeaf, cWls);
      // Tasks inherit the parent's shipped/due markers — they visually sit
      // inside the deliverable's planned span.
      group.children.push(cLeaf);
    }
    if (direct.length > 0) {
      const dLeaf = newNode(`${keyPrefix}${d.jira_issue_id}|direct`, '(logged on deliverable)', {
        jiraIssueKey: d.jira_issue_key,
        subLabel: 'Direct',
      });
      applyWorklogs(dLeaf, direct);
      group.children.push(dLeaf);
    }
    return group;
  }

  // Build leaves. Leaf semantics depend on mode.
  //
  // staff mode      → leaf = deliverable (user is implicit = subject).
  // team mode       → leaf = (member, deliverable).
  // engagement mode → leaf = deliverable — all authors' hours fold in; the
  //                   deliverable's own children (Packages / Tasks / child
  //                   issues) form the visible breakdown inside it.
  const leaves: Node[] = [];

  if (mode === 'staff' || mode === 'engagement') {
    // One (possibly-grouped) node per deliverable. With child issues, each
    // deliverable becomes a group containing one leaf per Package/Task.
    for (const d of deliverables) {
      const wls = worklogs.filter((w) => w.deliverable_jira_issue_id === d.jira_issue_id);
      leaves.push(makeDeliverableNode(d, wls, ''));
    }
  } else if (mode === 'team') {
    // One node per (user, deliverable). Expanded to per-task sub-leaves when
    // the deliverable has children.
    const touched = new Set<string>();
    for (const [k, wls] of wlByUserDeliv) {
      const [uid, did] = k.split('|');
      const d = delivById.get(did); if (!d) continue;
      touched.add(k);
      // Tag deactivated authors inline so they aren't mistaken for current
      // staff. Backend includes deactivated users in team-mode when they
      // have billable time in the window.
      const isActive = wls[0]?.author_is_active ?? true;
      const name = wls[0]?.author_display_name ?? uid;
      const uLabel = isActive ? name : `${name} (inactive)`;
      const node = makeDeliverableNode(d, wls, `${uid}|`, { subLabel: uLabel });
      leaves.push(node);
      (node as Node & { userId: string; userLabel: string }).userId = uid;
      (node as Node & { userId: string; userLabel: string }).userLabel = uLabel;
    }
    // Deliverables the team manages with no hours yet: pin them under the
    // DM/SA so they appear.
    for (const d of deliverables) {
      const managers = [d.delivery_manager_jira_user_id, d.solution_architect_jira_user_id].filter((x): x is string => !!x);
      for (const m of managers) {
        const k = `${m}|${d.jira_issue_id}`;
        if (touched.has(k)) continue;
        touched.add(k);
        const node = makeDeliverableNode(d, [], `${m}|`, { subLabel: m });
        leaves.push(node);
        (node as Node & { userId: string; userLabel: string }).userId = m;
        (node as Node & { userId: string; userLabel: string }).userLabel = m;
      }
    }
  }

  // Group leaves per mode.
  if (mode === 'staff' || mode === 'engagement') {
    // Engagement → Deliverable (with Package/Task children pre-built inside).
    // Staff and engagement share this shape; the only difference is which
    // worklogs went into each deliverable leaf (subject's vs. everyone's).
    const byEng = new Map<string, Node>();
    for (const leaf of leaves) {
      const d = delivById.get(leaf.key)!;
      const engId = d.engagement_jira_issue_id ?? '__none__';
      let eng = byEng.get(engId);
      if (!eng) {
        const e = engId !== '__none__' ? engById.get(engId) ?? null : null;
        eng = newNode(engId, e?.summary ?? d.engagement_summary ?? '(no engagement)', {
          shippedMarker: e?.completed_date ?? null,
        });
        byEng.set(engId, eng);
      }
      eng.children.push(leaf);
    }
    return aggregateAll(Array.from(byEng.values()).sort(byLabel));
  }

  if (mode === 'team') {
    const byMember = new Map<string, Node>();
    for (const leaf of leaves) {
      const uid = (leaf as Node & { userId: string }).userId;
      const uLabel = (leaf as Node & { userLabel: string }).userLabel;
      let m = byMember.get(uid);
      if (!m) { m = newNode(uid, uLabel); byMember.set(uid, m); }
      const d = delivById.get(leaf.key.split('|')[1]!)!;
      const engId = d.engagement_jira_issue_id ?? '__none__';
      let eg = m.children.find((c) => c.key === `${uid}|eng|${engId}`);
      if (!eg) {
        const e = engId !== '__none__' ? engById.get(engId) ?? null : null;
        eg = newNode(`${uid}|eng|${engId}`, e?.summary ?? d.engagement_summary ?? '(no engagement)', {
          shippedMarker: e?.completed_date ?? null,
        });
        m.children.push(eg);
      }
      eg.children.push(leaf);
    }
    // Hide members with zero billable time in the window. The Gantt API
    // returns `w.hours` = billable_hours (see routes/gantt.ts), so
    // totalHours after aggregation is strictly billable. Members whose
    // only contribution is non-billable work don't belong on a staff
    // utilisation timeline — they were just cluttering the left column.
    const aggregated = aggregateAll(Array.from(byMember.values()).sort(byLabel));
    return aggregated.filter((n) => n.totalHours > 0);
  }

  // Unreachable — staff/team/engagement are exhaustive. Kept as a typed
  // safety valve for future-mode additions.
  return aggregateAll(leaves.sort(byLabel));
}

function byLabel(a: Node, b: Node): number {
  return a.label.localeCompare(b.label);
}

/** Fold a leaf's worklogs into its aggregation fields. */
function applyWorklogs(node: Node, wls: GanttWorklog[]) {
  for (const w of wls) {
    node.totalHours += w.hours;
    node.totalValue += w.value;
    node.hoursByDate.set(w.work_date, (node.hoursByDate.get(w.work_date) ?? 0) + w.hours);
    node.earliestDate = minStr(node.earliestDate, w.work_date);
    node.latestDate   = maxStr(node.latestDate,   w.work_date);
    // First worklog anchors the actual start if the issue itself has no
    // actual_start field value.
    node.actualStart  = minStr(node.actualStart,  w.work_date);
    node.actualEnd    = maxStr(node.actualEnd,    w.work_date);
  }
}

/** Layer deliverable metadata (ship / due / start dates) onto a leaf. */
function applyDeliverable(node: Node, d: GanttDeliverable) {
  if (d.completed_date) {
    node.anyShipped = true;
    node.shippedMarker = d.completed_date;
    node.earliestDate = minStr(node.earliestDate, d.completed_date);
    node.latestDate   = maxStr(node.latestDate,   d.completed_date);
    node.actualEnd    = maxStr(node.actualEnd,    d.completed_date);
  } else {
    node.anyOpen = true;
  }
  if (d.due_date) {
    node.dueDate = d.due_date;
    node.earliestDate = minStr(node.earliestDate, d.due_date);
    node.latestDate   = maxStr(node.latestDate,   d.due_date);
    node.targetEnd    = maxStr(node.targetEnd,    d.due_date);
  }
  if (d.start_target) {
    node.targetStart = minStr(node.targetStart, d.start_target);
    node.earliestDate = minStr(node.earliestDate, d.start_target);
  }
  if (d.start_actual) {
    node.actualStart = minStr(node.actualStart, d.start_actual);
    node.earliestDate = minStr(node.earliestDate, d.start_actual);
  }
}

/** Recursively fold children into parents. Mutates nodes in place. */
function aggregate(node: Node): void {
  if (node.children.length === 0) return;
  for (const c of node.children) aggregate(c);
  for (const c of node.children) {
    node.totalHours += c.totalHours;
    node.totalValue += c.totalValue;
    node.earliestDate = minStr(node.earliestDate, c.earliestDate);
    node.latestDate   = maxStr(node.latestDate,   c.latestDate);
    node.targetStart  = minStr(node.targetStart,  c.targetStart);
    node.targetEnd    = maxStr(node.targetEnd,    c.targetEnd);
    node.actualStart  = minStr(node.actualStart,  c.actualStart);
    node.actualEnd    = maxStr(node.actualEnd,    c.actualEnd);
    if (c.anyOpen) node.anyOpen = true;
    if (c.anyShipped) node.anyShipped = true;
    if (!node.dueDate || (c.dueDate && c.dueDate > node.dueDate)) node.dueDate = c.dueDate ?? node.dueDate;
    for (const [d, h] of c.hoursByDate) {
      node.hoursByDate.set(d, (node.hoursByDate.get(d) ?? 0) + h);
    }
  }
  // If no explicit engagement-level shipped marker was set but every
  // descendant shipped, expose the latest shipped date as the group marker.
  if (!node.shippedMarker && node.anyShipped && !node.anyOpen) {
    node.shippedMarker = node.latestDate;
  }
}
function aggregateAll(nodes: Node[]): Node[] {
  for (const n of nodes) aggregate(n);
  return nodes;
}

// ─── Component ───────────────────────────────────────────────────────────────

const LABEL_COL_WIDTH = 400;
const ROW_HEIGHT = 28;
const HEADER_HEIGHT = 40;

export function GanttTimeline({ mode, from, to, worklogs, deliverables, childIssues, engagements }: Props) {
  const { config } = useConfig();

  const fromD = useMemo(() => toDate(from), [from]);
  const toD   = useMemo(() => toDate(to), [to]);
  const totalDays = daysBetween(fromD, toD) + 1;
  const todayYMD  = toYMD(new Date());

  const tree = useMemo(
    () => pivot(mode, worklogs, deliverables, childIssues, engagements),
    [mode, worklogs, deliverables, childIssues, engagements],
  );

  // Heat scale — consistent across every node for comparable colour saturation.
  const heatMax = useMemo(() => {
    let max = 0;
    const walk = (n: Node) => {
      for (const v of n.hoursByDate.values()) if (v > max) max = v;
      n.children.forEach(walk);
    };
    tree.forEach(walk);
    return max || 8;
  }, [tree]);

  // Expanded-node state. Default: top level expanded, deeper collapsed — so
  // the initial view shows structure without drowning in bars.
  const initialExpanded = useMemo(() => {
    const s = new Set<string>();
    for (const n of tree) s.add(n.key);
    return s;
  }, [tree]);
  const [expanded, setExpanded] = useState<Set<string>>(initialExpanded);

  function toggle(key: string) {
    setExpanded((prev) => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key); else n.add(key);
      return n;
    });
  }
  function expandAll() {
    const s = new Set<string>();
    const walk = (n: Node) => { s.add(n.key); n.children.forEach(walk); };
    tree.forEach(walk);
    setExpanded(s);
  }
  function collapseAll() {
    setExpanded(new Set(tree.map((n) => n.key)));
  }

  // Month ticks.
  const monthTicks: Array<{ label: string; dayIndex: number }> = useMemo(() => {
    const out: Array<{ label: string; dayIndex: number }> = [];
    let cursor = startOfMonth(fromD);
    if (daysBetween(fromD, cursor) < 0) cursor = addDays(cursor, 32);
    while (daysBetween(cursor, toD) >= 0) {
      const dayIndex = daysBetween(fromD, cursor);
      if (dayIndex >= 0) {
        out.push({
          label: cursor.toLocaleString('en-US', { month: 'short', year: '2-digit', timeZone: 'UTC' }),
          dayIndex,
        });
      }
      cursor = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 1));
    }
    return out;
  }, [fromD, toD]);

  if (tree.length === 0) {
    return <div style={{ padding: '32px 16px', fontSize: 12, color: 'var(--color-text-muted)', textAlign: 'center' }}>
      No deliverables or worklogs found in this window.
    </div>;
  }

  const pctOf = (date: string): number | null => {
    const idx = daysBetween(fromD, toDate(date));
    if (idx < 0 || idx > totalDays) return null;
    return (idx / totalDays) * 100;
  };

  // Flatten the visible tree into rows.
  interface Row { node: Node; depth: number }
  const rows: Row[] = [];
  const walk = (n: Node, depth: number) => {
    rows.push({ node: n, depth });
    if (expanded.has(n.key)) {
      for (const c of n.children) walk(c, depth + 1);
    }
  };
  for (const n of tree) walk(n, 0);
  const bodyHeight = rows.length * ROW_HEIGHT;

  return (
    <div style={{ border: '0.5px solid var(--color-border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', background: 'var(--color-surface)' }}>
      {/* Header */}
      <div style={{ display: 'grid', gridTemplateColumns: `${LABEL_COL_WIDTH}px 1fr`, borderBottom: '0.5px solid var(--color-border)' }}>
        <div style={{ padding: '10px 14px', fontSize: 11, fontWeight: 500, color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ flex: 1 }}>
            {mode === 'staff'      ? 'Engagement / Deliverable' :
             mode === 'team'       ? 'Member / Engagement / Deliverable' :
                                     'Engagement / Deliverable / Task'}
          </span>
          <button
            onClick={expandAll}
            style={{ background: 'none', border: '0.5px solid var(--color-border-strong)', borderRadius: 3, fontSize: 10, padding: '2px 6px', cursor: 'pointer' }}
          >
            Expand all
          </button>
          <button
            onClick={collapseAll}
            style={{ background: 'none', border: '0.5px solid var(--color-border-strong)', borderRadius: 3, fontSize: 10, padding: '2px 6px', cursor: 'pointer' }}
          >
            Collapse all
          </button>
        </div>
        <div style={{ position: 'relative', height: HEADER_HEIGHT, borderLeft: '0.5px solid var(--color-border)' }}>
          {monthTicks.map((t) => (
            <div
              key={t.dayIndex}
              style={{
                position: 'absolute',
                left: `${(t.dayIndex / totalDays) * 100}%`,
                top: 0, bottom: 0,
                borderLeft: '0.5px dashed var(--color-border)',
                fontSize: 10, color: 'var(--color-text-muted)',
                paddingLeft: 4, paddingTop: 12,
              }}
            >
              {t.label}
            </div>
          ))}
        </div>
      </div>

      {/* Body */}
      <div style={{ display: 'grid', gridTemplateColumns: `${LABEL_COL_WIDTH}px 1fr`, minHeight: bodyHeight }}>
        {/* Label column */}
        <div>
          {rows.map(({ node, depth }) => {
            const hasChildren = node.children.length > 0;
            const isExpanded = expanded.has(node.key);
            const url = node.jiraIssueKey ? jiraIssueUrl(config, node.jiraIssueKey) : null;
            return (
              <div
                key={node.key}
                onClick={hasChildren ? () => toggle(node.key) : undefined}
                style={{
                  height: ROW_HEIGHT,
                  display: 'flex', alignItems: 'center',
                  padding: `0 12px 0 ${12 + depth * 14}px`,
                  borderBottom: '0.5px solid var(--color-border)',
                  fontSize: 11,
                  cursor: hasChildren ? 'pointer' : 'default',
                  userSelect: 'none',
                  background: depth === 0 && hasChildren ? 'var(--color-border)' : 'transparent',
                }}
              >
                <span style={{ width: 12, display: 'inline-block', color: 'var(--color-text-muted)', textAlign: 'center', fontSize: 9 }}>
                  {hasChildren ? (isExpanded ? '▾' : '▸') : ''}
                </span>
                {node.issueType && <IssueTypeBadge issueType={node.issueType} />}
                {node.jiraIssueKey && (
                  url ? (
                    <a
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{ color: 'var(--color-blue)', fontSize: 10, marginRight: 6 }}
                    >
                      {node.jiraIssueKey}
                    </a>
                  ) : (
                    <span style={{ color: 'var(--color-text-muted)', fontSize: 10, marginRight: 6 }}>{node.jiraIssueKey}</span>
                  )
                )}
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: hasChildren ? 500 : 400 }}>
                  {node.label}
                  {node.subLabel && (
                    <span style={{ color: 'var(--color-text-muted)', marginLeft: 6, fontSize: 10 }}>
                      · {node.subLabel}
                    </span>
                  )}
                </span>
                <span style={{ fontSize: 10, color: 'var(--color-text-muted)', marginLeft: 6 }}>
                  {fmtHours(node.totalHours)}
                </span>
              </div>
            );
          })}
        </div>

        {/* Timeline column */}
        <div style={{ position: 'relative', borderLeft: '0.5px solid var(--color-border)' }}>
          {/* Month grid */}
          {monthTicks.map((t) => (
            <div
              key={`tick-${t.dayIndex}`}
              style={{
                position: 'absolute',
                left: `${(t.dayIndex / totalDays) * 100}%`,
                top: 0, bottom: 0,
                borderLeft: '0.5px dashed var(--color-border)',
                pointerEvents: 'none',
              }}
            />
          ))}
          {/* Today line */}
          {(() => {
            const p = pctOf(todayYMD);
            if (p === null) return null;
            return (
              <div style={{
                position: 'absolute', left: `${p}%`, top: 0, bottom: 0,
                borderLeft: '1px solid var(--color-brand-red)', pointerEvents: 'none',
              }} />
            );
          })()}

          {rows.map(({ node, depth }, i) => {
            const rowTop = i * ROW_HEIGHT;
            const hasChildren = node.children.length > 0;

            // Two bars per row (option C):
            //   Planned = targetStart → (dueDate or shippedMarker)
            //   Actual  = actualStart → actualEnd
            // Planned rendered as a thin outlined ghost strip on top; actual
            // rendered as the filled strip below. When either is missing we
            // fall back gracefully — a row with no target_start still shows
            // just the actual activity. If neither exists, skip.
            const plannedStart = node.targetStart;
            const plannedEnd   = node.targetEnd ?? node.shippedMarker;
            const actualStart  = node.actualStart;
            const actualEnd    = node.actualEnd ?? (node.anyShipped ? node.shippedMarker : todayYMD);
            const nothing = !plannedStart && !actualStart;
            if (nothing) return null;

            const isShipped = node.anyShipped && !node.anyOpen;
            const actualBorder = isShipped ? 'var(--color-green)' : 'var(--color-amber)';
            const actualFill   = isShipped ? 'var(--color-green-bg)' : 'var(--color-amber-bg)';

            const out: React.ReactNode[] = [];

            // Planned ghost bar — top half of the row.
            if (plannedStart && plannedEnd) {
              const sP = pctOf(plannedStart);
              const eP = pctOf(plannedEnd);
              if (sP !== null && eP !== null) {
                const left = Math.min(sP, eP);
                const width = Math.max(0.5, Math.abs(eP - sP));
                out.push(
                  <div
                    key={`pbar-${node.key}`}
                    title={`Planned: ${plannedStart} → ${plannedEnd}`}
                    style={{
                      position: 'absolute',
                      left: `${left}%`,
                      width: `${width}%`,
                      top: rowTop + 4,
                      height: 5,
                      border: '1px dashed var(--color-text-muted)',
                      borderRadius: 2,
                      background: 'transparent',
                      opacity: 0.75,
                    }}
                  />,
                );
              }
            }

            // Actual bar — middle of the row.
            if (actualStart && actualEnd) {
              const sP = pctOf(actualStart);
              const eP = pctOf(actualEnd);
              if (sP !== null && eP !== null) {
                const left = Math.min(sP, eP);
                const width = Math.max(0.5, Math.abs(eP - sP));
                out.push(
                  <div
                    key={`abar-${node.key}`}
                    title={`${node.label}\nActual: ${actualStart} → ${actualEnd}\nHours: ${fmtHours(node.totalHours)} · ${fmtCurrency(node.totalValue)}`}
                    style={{
                      position: 'absolute',
                      left: `${left}%`,
                      width: `${width}%`,
                      top: rowTop + 12,
                      height: 10,
                      background: hasChildren ? 'transparent' : actualFill,
                      border: `1px solid ${actualBorder}`,
                      borderRadius: 3,
                      opacity: depth === 0 ? 1 : 0.95,
                    }}
                  />,
                );
              }
            }

            // Density ticks sit on the actual bar.
            for (const [date, hrs] of node.hoursByDate) {
              const p = pctOf(date);
              if (p === null) continue;
              const intensity = Math.min(1, hrs / heatMax);
              out.push(
                <div
                  key={`h-${node.key}-${date}`}
                  title={`${date} — ${fmtHours(hrs)}`}
                  style={{
                    position: 'absolute',
                    left: `calc(${p}% - 1px)`,
                    width: 2,
                    top: rowTop + 14,
                    height: 6,
                    background: 'var(--color-brand-red)',
                    opacity: 0.25 + 0.6 * intensity,
                  }}
                />,
              );
            }

            if (node.shippedMarker) {
              const p = pctOf(node.shippedMarker);
              if (p !== null) out.push(
                <Marker key={`s-${node.key}`} left={p} top={rowTop + 17}
                        color="var(--color-green)" solid tooltip={`Shipped ${node.shippedMarker}`} />,
              );
            }
            if (node.dueDate && node.dueDate !== node.shippedMarker) {
              const p = pctOf(node.dueDate);
              if (p !== null) out.push(
                <Marker key={`u-${node.key}`} left={p} top={rowTop + 6}
                        color="var(--color-text-muted)" solid={false} tooltip={`Due ${node.dueDate}`} />,
              );
            }
            return <div key={`row-${node.key}`}>{out}</div>;
          })}
        </div>
      </div>
    </div>
  );
}

function Marker({ left, top, color, solid, tooltip }: { left: number; top: number; color: string; solid: boolean; tooltip: string }) {
  const size = 9;
  return (
    <div
      title={tooltip}
      style={{
        position: 'absolute',
        left: `calc(${left}% - ${size / 2}px)`,
        top: top - size / 2,
        width: size,
        height: size,
        background: solid ? color : 'transparent',
        border: `1.5px solid ${color}`,
        transform: 'rotate(45deg)',
        pointerEvents: 'auto',
      }}
    />
  );
}
